import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AllAlarms.css';
import './GroupDetails.css';
import { API_BASE, authHeaders } from '../constants/api';
import { severityLabels } from '../constants/alarmConstants';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { updateAlarmStatusAPI } from '../utils/alarmActions';
import { formatDateDE } from '../utils/formatDate';
import Pagination from './Pagination';
import SearchWithSuggestions from './SearchWithSuggestions';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import { useStatusMessage } from '../hooks/useStatusMessage';
import { StatusBar, DeleteConfirmModal, SortDropdown, IconButton, LoadingErrorState, PageToolbar, AlarmStatusDropdown } from '../UI-Elements';

const PAGE_SIZE = 10;

const _ALARM_DEDUPE_MS = 500;

function AllAlarms() {
  const [alarmsCount, setAlarmsCount] = useState(null);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filterOption, setFilterOption] = useState('createdTime');
  const [openAlarmStatusMenu, setOpenAlarmStatusMenu] = useState(null);
  const deviceNameCacheRef = useRef(new Map());
  const [originatorNames, setOriginatorNames] = useState({});

  const { statusMessage, statusType, showStatus } = useStatusMessage();

  const [alarmToDelete, setAlarmToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const dedupeRef = useRef({ key: null, time: 0 });

  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  const closeAlarmStatusMenu = useCallback(() => setOpenAlarmStatusMenu(null), []);
  useDropdownClose(openAlarmStatusMenu, closeAlarmStatusMenu, '.alarm-status-wrapper');

  useEffect(() => {
    async function loadAlarms() {
      try {
        setLoading(true);
        setError("");

        const key = `${token || ''}|${page}|${filterOption}|${searchTerm || ''}`;
        const now = Date.now();
        if (dedupeRef.current.key === key && now - dedupeRef.current.time < _ALARM_DEDUPE_MS) {
          return;
        }
        dedupeRef.current = { key, time: now };

        const params = new URLSearchParams();
        params.append('searchStatus', 'ANY');
        params.append('pageSize', String(PAGE_SIZE));
        params.append('page', String(page));
        // Only request server-side sorting for createdTime (backend-supported).
        if (filterOption === 'createdTime') {
          params.append('sortProperty', 'createdTime');
          params.append('sortOrder', 'DESC');
        } else {
          // keep server sort stable when doing client-side sorts
          params.append('sortOrder', 'DESC');
        }
        params.append('fetchOriginator', 'true');
        if (searchTerm) {
          params.append("textSearch", searchTerm);
        }
        const res = await fetch(
          `${API_BASE}/alarms?${params.toString()}`,
          {
            headers: authHeaders(token)
          }
        );
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Fehler beim Laden der Alarme (HTTP ${res.status})`);
        }
        const data = await res.json();
        let items = data.data || [];

        // If backend doesn't support sorting by certain properties, sort client-side.
        if (filterOption && filterOption !== 'createdTime' && items.length > 0) {
          if (filterOption === 'name') {
            items.sort((a, b) => {
              const na = (a && a.name) ? String(a.name) : '';
              const nb = (b && b.name) ? String(b.name) : '';
              return na.localeCompare(nb, undefined, { sensitivity: 'base' });
            });
          } else if (filterOption === 'severity') {
            const order = { CRITICAL: 0, MAJOR: 1, MINOR: 2, WARNING: 3, INDETERMINATE: 4 };
            items.sort((a, b) => (order[a && a.severity] ?? 99) - (order[b && b.severity] ?? 99));
          } else if (filterOption === 'status') {
            const order = { ACTIVE_UNACK: 0, ACTIVE_ACK: 1, CLEARED_ACK: 2 };
            items.sort((a, b) => (order[a && a.status] ?? 99) - (order[b && b.status] ?? 99));
          }
        }

        setAlarms(items);
        setAlarmsCount(data.totalElements);
        setTotalPages(data.totalPages);
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Alarme. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setAlarms([]);
        setAlarmsCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }
    if (token) {
      loadAlarms();
    }
  }, [token, page, searchTerm, filterOption, reloadKey]);

  const suggestions = searchTerm
    ? (() => {
        const seen = new Set();
        const list = [];
        for (const d of alarms || []) {
          const name = (d && d.name) ? String(d.name) : '';
          if (!name) continue;
          if (name.toLowerCase().includes(searchTerm.toLowerCase()) && !seen.has(name)) {
            seen.add(name);
            list.push({ id: d.id, name });
            if (list.length >= 5) break;
          }
        }
        return list;
      })()
    : [];

  const fetchDeviceNameIfNeeded = useCallback(async function fetchDeviceNameIfNeeded(originator) {
    if (!originator) return null;
    // originator may be string id or object
    const id = originator?.id?.id ?? originator?.id ?? originator;
    if (!id) return null;
    const key = String(id);
    if (deviceNameCacheRef.current.has(key)) return deviceNameCacheRef.current.get(key);

    // best-effort: try couple of candidate endpoints
    const candidates = [
      `${API_BASE}/device/${key}`,
      `${API_BASE}/devices/${key}`,
      `${API_BASE}/tenant/devices/${key}`
    ];
    for (const url of candidates) {
      try {
        const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
        if (!res.ok) continue;
        const data = await res.json().catch(() => null);
        const name = data && (data.name || data.deviceName || data.label || data.title);
        if (name) {
          deviceNameCacheRef.current.set(key, name);
          setOriginatorNames(prev => ({ ...prev, [key]: name }));
          return name;
        }
      } catch (e) {
        // ignore and try next
      }
    }

    // fallback to id
    deviceNameCacheRef.current.set(key, key);
    setOriginatorNames(prev => ({ ...prev, [key]: key }));
    return key;
  }, [token]);

  // prefetch originator names for shown alarms
  useEffect(() => {
    const idsToFetch = [];
    for (const a of alarms || []) {
      const id = a?.originator?.id?.id ?? a?.originator?.id ?? a?.originator;
      const key = id && String(id);
      if (key && !deviceNameCacheRef.current.has(key)) idsToFetch.push(key);
    }
    if (idsToFetch.length === 0) return;
    // fetch sequentially to avoid spamming
    (async () => {
      for (const k of idsToFetch) {
        try {
          await fetchDeviceNameIfNeeded({ id: k });
        } catch (_) {}
      }
    })();
  }, [alarms, fetchDeviceNameIfNeeded]);

  async function ackAlarm(alarm) {
    await updateAlarmStatus(alarm, "ack", "ACTIVE_ACK");
  }

  async function clearAlarm(alarm) {
    await updateAlarmStatus(alarm, "clear", "CLEARED_ACK");
  }

  async function updateAlarmStatus(alarm, action, newStatus) {
    try {
      await updateAlarmStatusAPI(alarm.id.id, action, token);

      setAlarms(prev =>
        prev.map(a =>
          a.id.id === alarm.id.id ? { ...a, status: newStatus } : a
        )
      );

      setOpenAlarmStatusMenu(null);

      const labels = { ACTIVE_ACK: 'Alarm bestätigt', CLEARED_ACK: 'Alarm behoben' };
      showStatus(labels[newStatus] || 'Status geändert');
    } catch (err) {
      showStatus(`Alarmstatus konnte nicht geändert werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    }
  }

  async function openAlarmRule(alarm) {
    try {
      const originatorId = alarm?.originator?.id?.id ?? alarm?.originator?.id ?? alarm?.originator;
      const params = new URLSearchParams({
        pageSize: '50',
        page: '0',
        types: 'ALARM',
        sortProperty: 'createdTime',
        sortOrder: 'DESC',
        textSearch: alarm.type || alarm.name || '',
      });
      const res = await fetch(`${API_BASE}/calculatedFields?${params.toString()}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht geladen werden (HTTP ${res.status})`);
      }
      const data = await res.json();
      const rules = data.data || [];
      // find the rule whose name matches the alarm type and whose entity matches the originator
      const match = rules.find(r =>
        r.name === (alarm.type || alarm.name) &&
        (r.entityId?.id === originatorId || !originatorId)
      ) || rules.find(r => r.name === (alarm.type || alarm.name));
      if (match) {
        navigate(`/alarm-rules/${match.id?.id}`);
      } else {
        showStatus('Zugehörige Alarmregel nicht gefunden.', 'error');
      }
    } catch (err) {
      showStatus(`Alarmregel konnte nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    }
  }

  async function deleteAlarm() {
    if (!alarmToDelete) return;
    try {
      setDeleting(true);
      const res = await fetch(`${API_BASE}/alarm/${alarmToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarm konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      showStatus(`Alarm „${alarmToDelete.name}" wurde erfolgreich gelöscht.`);
      setReloadKey(k => k + 1);
    } catch (err) {
      showStatus(`Alarm konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setAlarmToDelete(null);
    }
  }

    /* ===============================
      Render
    =============================== */

  return (
    <div className="all-alarms-page">
      {/* Toolbar */}
      <PageToolbar
        left={
          <>
            <h1 className="device-title">Alle ausgelösten Alarme ({alarmsCount ?? "..."})</h1>
            <SortDropdown
              options={[
                { key: 'name', label: 'Name' },
                { key: 'createdTime', label: 'Ausgelöst' },
                { key: 'status', label: 'Status' },
                { key: 'severity', label: 'Schweregrad' },
              ]}
              value={filterOption}
              onChange={(key) => { setFilterOption(key); setPage(0); }}
            />
          </>
        }
        right={
          <SearchWithSuggestions
            ref={searchInputRef}
            id="device-search"
            label="Ausgelösten Alarm suchen"
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setPage(0); }}
            suggestions={suggestions}
            suggestionsId="device-suggestions"
          />
        }
      />

      <StatusBar message={statusMessage} statusType={statusType} />

      {/* Liste */}
      <div className="device-table-container" role="region" aria-label="Alarme" aria-busy={loading}>
        <LoadingErrorState loading={loading} loadingText="Alarme werden geladen…" error={error}>
          <>
            <div className="device-list" role="list" aria-label="Alarme">
              <div className="device-row device-header" role="presentation" aria-hidden="true">
                <div>Bezeichnung</div><div>Ausgelöst</div><div>Status</div><div>Öffnen</div><div>Löschen</div>
              </div>
              {(!alarms || alarms.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Alarme gefunden</div>
              ) : alarms.map(a => (
                <div key={a.id?.id ?? a.id} className="list-item device-item alarm-item clickable-row" role="listitem"
                  tabIndex={0} aria-label={`Alarm ${a.name} öffnen`}
                  onClick={(e) => { if (e.target.closest('button, .sort-wrapper, .originator-link')) return; openAlarmRule(a); }}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper, .originator-link')) { e.preventDefault(); openAlarmRule(a); } }}
                >
                  <div>
                    <span className="alarm-name">{a.name}</span>
                    <span className={`severity-badge ${a.severity ? a.severity.toLowerCase() : ''}`}>{severityLabels[a.severity]}</span>
                  </div>
                  <div>{formatDateDE(a.createdTime)} von {(() => {
                    const id = a?.originator?.id?.id ?? a?.originator?.id ?? a?.originator;
                    const key = id && String(id);
                    const name = (key && originatorNames[key]) ? originatorNames[key] : (a?.originator?.name || key || 'Unbekannt');
                    return key ? (
                      <button type="button" className="originator-link" title={`Gerät ${name} öffnen`} aria-label={`Gerät ${name} öffnen`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/devices/${key}`, { state: { name } }); }}>{name}</button>
                    ) : name;
                  })()}</div>
                  <div className="actions">
                    <AlarmStatusDropdown
                      alarmId={a.id?.id ?? a.id}
                      status={a.status}
                      isOpen={openAlarmStatusMenu === (a.id?.id ?? a.id)}
                      onToggle={() => setOpenAlarmStatusMenu(openAlarmStatusMenu === (a.id?.id ?? a.id) ? null : (a.id?.id ?? a.id))}
                      onAck={() => ackAlarm(a)}
                      onClear={() => clearAlarm(a)}
                      onClose={() => setOpenAlarmStatusMenu(null)}
                    />
                  </div>
                  <div>
                    <IconButton variant="open" label="Zugehörige Alarmregel öffnen" onClick={() => openAlarmRule(a)} />
                  </div>
                  <div>
                    <IconButton variant="delete" label={`Alarm ${a.name} löschen`}
                      onClick={() => { setAlarmToDelete({ id: a.id?.id ?? a.id, name: a.name }); setShowDeleteConfirm(true); }} />
                  </div>
                </div>
              ))}
            </div>
            {alarmsCount > 0 && <Pagination page={page} totalPages={totalPages} totalItems={alarmsCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Alarme" />}
          </>
        </LoadingErrorState>
      </div>

      {showDeleteConfirm && alarmToDelete && (
        <DeleteConfirmModal
          titleId="delete-alarm-title"
          title="Alarm löschen?"
          body={<p>Soll der Alarm <i>{alarmToDelete.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>. Das Löschen hat keine Auswirkung auf die zugrunde liegende Alarmregel.</p>}
          onConfirm={deleteAlarm}
          onCancel={() => { setShowDeleteConfirm(false); setAlarmToDelete(null); }}
          loading={deleting}
        />
      )}
    </div>
  );
}

export default function AllAlarmsWithBoundary() {
  return <ErrorBoundary><AllAlarms /></ErrorBoundary>;
}
